import history from "App/History";
import { useContext, useEffect, useMemo, useState } from "react";
import Arrow from "shared/Icons/Arrow";
import ParticipantInfoCard from "components/ParticipantInfoCard";
import EditParticipant from "components/EditParticipant";
import { useQuery } from "shared/hooks/useQuery";
import { useTypedDispatch, useTypedSelector } from "App/Store";
import {
  updateTeamCheckIn,
  updateTeamData,
} from "core/services/TournamentBracket";
import { useParams } from "react-router-dom";
import { hideLoader, showLoader } from "redux/action/loaderAction";
import { getParticipantInfoList } from "redux/action/participantInfoAction";
import { DEFAULT_API_ERROR } from "core/constants/Defaults";
import { NotifierContext } from "App/Notifier";
import { Button, Dropdown, Input, InputGroup } from "rsuite";
import LegacyArrowDownLineIcon from "@rsuite/icons/legacy/ArrowDownLine";
import clsx from "clsx";
import LegacySearchIcon from "@rsuite/icons/legacy/Search";
import { tournamentDetails } from "redux/action/tournamentDetailsAction";

interface UrlParams {
  tournamentId: string;
}

export default function ParticipantInfo() {
  const { tournament } = useTypedSelector((state) => state.tournament);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [editableParticipant, setEditableParticipant] = useState<any>(null);
  const query = useQuery();
  const { isLoading, tournamentTeams } = useTypedSelector(
    (state) => state.participantInfo
  );
  const urlParams = useParams<UrlParams>();
  const dispatch = useTypedDispatch();
  const [participantInfos, setParticipantInfos] = useState<any[]>([]);
  const { showNotification } = useContext(NotifierContext);
  const [selectedType, setSelectedType] = useState<
    "All" | "CheckedIn" | "Not CheckedIn"
  >("All");
  const [openFilterDropdown, setOpenFilterDropdown] = useState(false);
  const [search, setSearch] = useState("");
  const [resolvedTournamentType, setResolvedTournamentType] = useState<
    string | null
  >(null);

  // Fetch participants on mount
  useEffect(() => {
    dispatch(getParticipantInfoList(urlParams.tournamentId));
  }, [dispatch, query, urlParams]);
  useEffect(() => {
    dispatch(tournamentDetails(urlParams.tournamentId));
  }, [dispatch, urlParams.tournamentId]);

  // Update participantInfos when tournamentTeams changes
  useEffect(() => {
    setParticipantInfos(tournamentTeams || []);
  }, [tournamentTeams]);
  useEffect(() => {
    if (tournament?.type && resolvedTournamentType === null) {
      setResolvedTournamentType(tournament.type);
    }
  }, [tournament?.type, resolvedTournamentType]);

  // Show / hide loader
  useEffect(() => {
    if (isLoading) {
      dispatch(showLoader());
    } else {
      dispatch(hideLoader());
    }
  }, [isLoading, dispatch]);
  const canRenderTable = resolvedTournamentType !== null;

  const isBattleRoyale =
    resolvedTournamentType?.toLowerCase().includes("battle") ?? false;
  // Derived filtered participants (based on type and search)
  const filteredParticipants = useMemo(() => {
    return participantInfos
      .filter((team) => {
        if (isBattleRoyale) return true;
        if (selectedType === "CheckedIn") return team.checkedIn === true;
        if (selectedType === "Not CheckedIn") return team.checkedIn === false;
        return true;
      })
      .filter((team) =>
        team.name.toLowerCase().includes(search.trim().toLowerCase())
      );
  }, [participantInfos, selectedType, search, isBattleRoyale]);

  // Handle check-in update
  function handleUpdateCheckIn(teamId: number, checkIn: boolean) {
    dispatch(showLoader());

    const payload = {
      tournamentId: urlParams.tournamentId,
      teamId,
      checkIn,
    };

    updateTeamCheckIn(payload).then((res) => {
      if (res.status) {
        showNotification({
          message: "Team Check-In Updated Successfully",
          title: "Success",
          type: "success",
        });

        // Update main participantInfos
        setParticipantInfos((prev) =>
          prev.map((p) =>
            p.teamId === teamId ? { ...p, checkedIn: checkIn } : p
          )
        );

        setOpenEditModal(false);
      } else {
        showNotification({
          message: res.message || DEFAULT_API_ERROR,
          title: "Error",
          type: "error",
        });
      }

      dispatch(hideLoader());
    });
  }

  // Handle team edit
  function handleEdit(teamId: number) {
    const found = participantInfos.find((p) => p.teamId === teamId);
    if (found) {
      setEditableParticipant(JSON.parse(JSON.stringify(found))); // deep copy
      setOpenEditModal(true);
    } else {
      setEditableParticipant(null);
      setOpenEditModal(false);
    }
  }

  function handleCloseEditModal() {
    setOpenEditModal(false);
    setEditableParticipant(null);
  }

  function handleMemberChange(index: number, field: string, value: string) {
    setEditableParticipant((prev: any) => {
      const updated = { ...prev };
      updated.members[index] = { ...updated.members[index], [field]: value };
      return updated;
    });
  }

  function handleCustomFieldChange(
    memberIndex: number,
    fieldIndex: number,
    fieldKey: string,
    value: string
  ) {
    setEditableParticipant((prev: any) => {
      const updated = { ...prev };
      const member = updated.members[memberIndex];
      const newFields = [...member.customFields];
      newFields[fieldIndex] = {
        ...newFields[fieldIndex],
        [fieldKey]: value,
      };
      member.customFields = newFields;
      updated.members[memberIndex] = member;
      return updated;
    });
  }

  function handleSaveChanges() {
    if (!editableParticipant) return;

    dispatch(showLoader());

    const payload = {
      teamId: editableParticipant.teamId,
      participants: editableParticipant.members.map((member: any) => ({
        userId: member.userId,
        inGameName: member.inGameName,
        ...(member.inGameId ? { inGameId: member.inGameId } : {}),
        ...(member.customFields?.length
          ? {
              customFields: member.customFields.map((field: any) => ({
                ...field,
              })),
            }
          : {}),
      })),
    };

    updateTeamData(urlParams.tournamentId, payload).then((res) => {
      if (res.status) {
        showNotification({
          message: "Team Updated Successfully",
          title: "Success",
          type: "success",
        });

        setParticipantInfos((prev) =>
          prev.map((p) =>
            p.teamId === editableParticipant.teamId ? editableParticipant : p
          )
        );

        setOpenEditModal(false);
      } else {
        showNotification({
          message: res.message || DEFAULT_API_ERROR,
          title: "Error",
          type: "error",
        });
      }

      dispatch(hideLoader());
    });
  }

  function handleRedirection() {
    history.goBack();
  }

  function handleFilterDropdown(eve: any) {
    eve.stopPropagation();
    setOpenFilterDropdown((prev) => !prev);
  }

  function handleSelect(type: "All" | "CheckedIn" | "Not CheckedIn") {
    setSelectedType(type);
    setOpenFilterDropdown(false);
  }

  return (
    <div className="paricipant-info-wrapper">
      <div className="paricipant-info-title-wrapper">
        <div className="paricipant-info-title-info">
          <span className="form-back-btn" onClick={handleRedirection}>
            <Arrow fontSize={32} />
          </span>
          <h4>Participants Info</h4>
        </div>
        <div className="filter-wrapper">
          {!isBattleRoyale && (
            <>
              <div className="filter-title">Filter By</div>
              <div>
                <Dropdown
                  renderToggle={() => (
                    <Button
                      onClick={(eve) => handleFilterDropdown(eve)}
                      appearance="subtle"
                      className={clsx("mods-menu", "subtle-btn")}
                    >
                      {selectedType}
                      <LegacyArrowDownLineIcon color="#FFFFFF" />
                    </Button>
                  )}
                  placement="bottomEnd"
                  className="participant-menu"
                  open={openFilterDropdown}
                  onClose={() => setOpenFilterDropdown(false)}
                >
                  <Dropdown.Item onClick={() => handleSelect("All")}>
                    all
                  </Dropdown.Item>
                  {!isBattleRoyale && (
                    <>
                      <Dropdown.Item onClick={() => handleSelect("CheckedIn")}>
                        CheckedIn
                      </Dropdown.Item>
                      <Dropdown.Item
                        onClick={() => handleSelect("Not CheckedIn")}
                      >
                        Not CheckedIn
                      </Dropdown.Item>
                    </>
                  )}
                </Dropdown>
              </div>
            </>
          )}
          <div>
            <InputGroup>
              <Input
                value={search}
                onChange={setSearch}
                // onPressEnter={}
                placeholder="Search Team"
              />
              <InputGroup.Button>
                <LegacySearchIcon />
              </InputGroup.Button>
            </InputGroup>
          </div>
        </div>
      </div>
      {canRenderTable && (
        <div className="table-container">
          <div className={`request-header ${isBattleRoyale ? "br" : "vs"}`}>
            <div className="reg-title">Reg</div>
            <div className="palyer-title">Team Name</div>
            <div className="in-game-name-title">MemberCount</div>
            {!isBattleRoyale && <div className="checkIn-title">Check In</div>}
            <div className="action-title">Action</div>
          </div>

          {!!filteredParticipants?.length ? (
            filteredParticipants?.map((participantInfo, index) => (
              <ParticipantInfoCard
                key={index}
                reg={index}
                teamId={participantInfo?.teamId}
                teamName={participantInfo?.name}
                memberCount={participantInfo?.members?.length}
                onEdit={handleEdit}
                checkedIn={
                  !isBattleRoyale ? participantInfo?.checkedIn : undefined
                }
                handleUpdateCheckIn={
                  !isBattleRoyale ? handleUpdateCheckIn : undefined
                }
                isBattleRoyale={isBattleRoyale}
              />
            ))
          ) : (
            <div className="dummy-text">
              <span>No Participant Info Card Available</span>
            </div>
          )}
        </div>
      )}
      <EditParticipant
        open={openEditModal}
        participant={editableParticipant}
        onClose={handleCloseEditModal}
        onMemberChange={handleMemberChange}
        onCustomFieldChange={handleCustomFieldChange}
        onSave={handleSaveChanges}
      />
    </div>
  );
}
